"use client";
import React, { createContext, useContext, useState } from "react";

export type CatalogResultsState = {
  /** Items currently loaded in the grid (initial page + load-more pages). */
  loadedCount: number | null;
  /** Total results for the active query. */
  total: number | null;
  pageSize: number | null;
  currentPage: number | null;
};

const initialState: CatalogResultsState = {
  loadedCount: null,
  total: null,
  pageSize: null,
  currentPage: null,
};

/**
 * Two contexts on purpose. The grid only needs `publish` — a bare setState
 * whose identity never changes — so subscribing to the publish context must
 * never re-render the grid when counts change. Re-rendering the grid on every
 * publish/shell render is what re-triggers VirtuosoGrid recalculation and the
 * ItemCard image flicker the GPID-key/overscan work exists to prevent. The
 * chrome (sidebar count line, mobile strip, mobile filter button) consumes the
 * data context, whose value is the state object itself (stable between
 * publishes).
 */
const CatalogResultsDataContext = createContext<CatalogResultsState>(initialState);
const CatalogResultsPublishContext = createContext<
  React.Dispatch<React.SetStateAction<CatalogResultsState>>
>(() => {});

/**
 * Bridges result counts from the streamed grid (inside the Suspense boundary)
 * up to the chrome that renders before the items resolve. Counts are null
 * until the grid mounts and publishes.
 */
export function CatalogResultsProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CatalogResultsState>(initialState);
  return (
    <CatalogResultsPublishContext.Provider value={setState}>
      <CatalogResultsDataContext.Provider value={state}>
        {children}
      </CatalogResultsDataContext.Provider>
    </CatalogResultsPublishContext.Provider>
  );
}

/** Read the published counts (chrome consumers — re-renders on publish). */
export function useCatalogResults() {
  return useContext(CatalogResultsDataContext);
}

/** Write channel for the grid — stable identity, never causes re-renders. */
export function usePublishCatalogResults() {
  return useContext(CatalogResultsPublishContext);
}
